/**
 * authStorage.ts — centralized facade for auth session storage.
 *
 * All auth/user data is stored in sessionStorage (scoped to the current tab).
 * This closes the XSS token-theft vector that existed when tokens were in
 * localStorage (accessible to any script, across all tabs, after page unload).
 *
 * Consumers must import from here — never call sessionStorage directly.
 */
import type { AuthData, UserData } from '@/composables/useCurrentUser'

const AUTH_KEY = 'auth'
const USER_KEY = 'user'

export function readAuthData(): AuthData | null {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed.id !== 'number' || typeof parsed.isAuthenticated !== 'boolean') return null
    return parsed as AuthData
  } catch {
    return null
  }
}

export function readUserData(): UserData | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed.id !== 'number' || typeof parsed.role !== 'string') return null
    return parsed as UserData
  } catch {
    return null
  }
}

/** Shortcut: returns the stored idToken, or '' if not available. */
export function getStoredAuthToken(): string {
  return readAuthData()?.idToken ?? ''
}

/** Shortcut: returns the stored user ID, or null if not available. */
export function getStoredUserId(): number | null {
  return readAuthData()?.id ?? null
}

export function writeAuthData(data: AuthData): void {
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(data))
}

export function writeUserData(data: UserData): void {
  sessionStorage.setItem(USER_KEY, JSON.stringify(data))
}

export function clearAuthStorage(): void {
  sessionStorage.removeItem(AUTH_KEY)
  sessionStorage.removeItem(USER_KEY)
}

/**
 * One-time migration: removes stale auth/user keys from localStorage on first
 * load after this deploy. Does NOT copy values to sessionStorage — forces
 * re-login so tokens are validated fresh. Safe to call on every app start;
 * it is a no-op once the keys are gone.
 */
export function migrateFromLocalStorage(): void {
  if (localStorage.getItem(AUTH_KEY) !== null) {
    localStorage.removeItem(AUTH_KEY)
  }
  if (localStorage.getItem(USER_KEY) !== null) {
    localStorage.removeItem(USER_KEY)
  }
}
